import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDropzone } from 'react-dropzone'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Upload, File, X } from 'lucide-react'
import { useDatasetStore } from '@/store/datasetStore'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { uploadDatasetSchema, validateFile, type UploadDatasetInput } from '@/schemas/dataset.schema'
import { showErrorToast, showSuccessToast } from '@/utils/error-handler'
import { useTranslation } from 'react-i18next'
import { ColumnMappingModal, type ColumnMapping } from '@/components/datasets/ColumnMappingModal'
import { datasetsApi } from '@/services/api/datasets'

export default function DatasetUploadPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { uploadDataset, isLoading } = useDatasetStore()
  const [file, setFile] = useState<File | null>(null)
  const [fileError, setFileError] = useState<string | null>(null)
  const [showMappingModal, setShowMappingModal] = useState(false)
  const [csvColumns, setCsvColumns] = useState<string[]>([])
  const [columnMapping, setColumnMapping] = useState<ColumnMapping | null>(null)
  const [, setLoadingColumns] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<UploadDatasetInput>({
    resolver: zodResolver(uploadDatasetSchema),
    defaultValues: {
      name: '',
      symbol: '',
      timeframe: '',
    },
  })

  const name = watch('name')

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'text/csv': ['.csv'],
    },
    onDrop: async (acceptedFiles, rejectedFiles) => {
      setFileError(null)
      if (rejectedFiles.length > 0) {
        const error = 'Invalid file type. Please upload a CSV file.'
        setFileError(error)
        showErrorToast(new Error(error))
        return
      }
      if (acceptedFiles.length > 0) {
        const selectedFile = acceptedFiles[0]
        const validationError = validateFile(selectedFile)
        if (validationError) {
          setFileError(validationError)
          showErrorToast(new Error(validationError))
          return
        }
        setFile(selectedFile)
        if (!name) {
          setValue('name', selectedFile.name.replace('.csv', ''))
        }
        
        // Load CSV columns and show mapping modal
        try {
          setLoadingColumns(true)
          const preview = await datasetsApi.previewColumns(selectedFile)
          setCsvColumns(preview.columns)
          setShowMappingModal(true)
        } catch (error) {
          showErrorToast(error, 'Failed to read CSV columns')
        } finally {
          setLoadingColumns(false)
        }
      }
    },
  })

  const handleMappingConfirm = (mapping: ColumnMapping) => {
    setColumnMapping(mapping)
    setShowMappingModal(false)
  }

  const handleMappingCancel = () => {
    setShowMappingModal(false)
    setFile(null)
    setColumnMapping(null)
  }

  const onSubmit = async (data: UploadDatasetInput) => {
    const validationError = validateFile(file)
    if (validationError) {
      setFileError(validationError)
      showErrorToast(new Error(validationError))
      return
    }

    if (!file) {
      const error = 'Please select a file'
      setFileError(error)
      showErrorToast(new Error(error))
      return
    }

    if (!columnMapping) {
      const error = 'Please map CSV columns first'
      setFileError(error)
      showErrorToast(new Error(error))
      return
    }

    try {
      const metadata: Record<string, unknown> = {}
      if (data.symbol) metadata.symbol = data.symbol
      if (data.timeframe) metadata.timeframe = data.timeframe
      metadata.columnMapping = columnMapping

      const dataset = await uploadDataset(file, data.name, metadata)
      showSuccessToast(t('datasets.uploadSuccess'))
      navigate(`/datasets/${dataset.id}`)
    } catch (error) {
      showErrorToast(error, t('errors.uploadDataset'))
    }
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold">{t('datasets.upload')}</h1>
        <p className="text-muted-foreground mt-1">
          Upload a CSV file with OHLCV market data
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Card className="mb-4">
          <CardHeader>
            <CardTitle>File</CardTitle>
            <CardDescription>Select or drag a CSV file</CardDescription>
          </CardHeader>
          <CardContent>
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                isDragActive
                  ? 'border-primary bg-primary/5'
                  : 'border-muted-foreground/25 hover:border-primary/50'
              }`}
            >
              <input {...getInputProps()} />
              {file ? (
                <div className="flex items-center justify-center gap-2">
                  <File className="w-8 h-8 text-primary" />
                  <div className="flex-1 text-left">
                    <p className="font-medium">{file.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={(e) => {
                      e.stopPropagation()
                      setFile(null)
                    }}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div>
                  <Upload className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    {isDragActive
                      ? t('datasets.dropFile')
                      : t('datasets.dragDrop')}
                  </p>
                </div>
              )}
            </div>
            {fileError && (
              <p className="text-sm text-destructive mt-2">{fileError}</p>
            )}
          </CardContent>
        </Card>

        <Card className="mb-4">
          <CardHeader>
            <CardTitle>{t('datasets.metadata')}</CardTitle>
            <CardDescription>{t('datasets.provideInfo')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">{t('datasets.datasetName')} *</Label>
              <Input
                id="name"
                {...register('name')}
                placeholder="e.g., NQ Futures 5min"
              />
              {errors.name && (
                <p className="text-sm text-destructive">{errors.name.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="symbol">{t('datasets.symbol')}</Label>
              <Input
                id="symbol"
                {...register('symbol')}
                placeholder={t('datasets.symbolPlaceholder')}
              />
              {errors.symbol && (
                <p className="text-sm text-destructive">{errors.symbol.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="timeframe">{t('datasets.timeframe')}</Label>
              <Input
                id="timeframe"
                {...register('timeframe')}
                placeholder={t('datasets.timeframePlaceholder')}
              />
              {errors.timeframe && (
                <p className="text-sm text-destructive">{errors.timeframe.message}</p>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="flex gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/datasets')}
          >
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={!file || !name || !columnMapping || isLoading}>
            {isLoading ? t('datasets.uploading') : t('datasets.upload')}
          </Button>
        </div>
      </form>

      <ColumnMappingModal
        open={showMappingModal}
        onOpenChange={setShowMappingModal}
        csvColumns={csvColumns}
        onConfirm={handleMappingConfirm}
        onCancel={handleMappingCancel}
      />
    </div>
  )
}
